// src/components/layout/BellhoptLogo.js
//
// Single source of truth for the Bellhopt brand mark in the admin shell.
// Two visual variants live here, both inlined as React SVGs so they can
// inherit the surrounding text/gradient color without an external <img>:
//
//   - <BellhoptLogo variant="mark" />     — square 36×36 brand tile
//   - <BellhoptLogo variant="wordmark" /> — horizontal gradient pill
//
// These replace the legacy `<span>B</span>` / literal "Bellhopt" text
// placeholders so the shell reads as a real product identity instead of
// a single letter on a colored square. Surrounding container sizes,
// gradients, shadows, and spacing are preserved by callers — this
// component just owns the inner mark.

import React from 'react';

// Stylized "B" mark + a small dot reminiscent of a bell-hop bell.
function MarkSvg({ className = 'h-5 w-5', title = 'Bellhopt' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <title>{title}</title>
      <path d="M22 16h13.5c5.6 0 9.3 3.1 9.3 7.7 0 3-1.6 5.2-4.4 6.3 3.5 1 5.6 3.5 5.6 7 0 5.1-4 8.4-10 8.4H22V16Zm5.7 12.1h7.4c2.7 0 4.3-1.3 4.3-3.4 0-2-1.6-3.3-4.3-3.3h-7.4v6.7Zm0 12.4h7.9c2.9 0 4.6-1.4 4.6-3.6 0-2.2-1.7-3.6-4.6-3.6h-7.9v7.2Z" />
      <circle cx="46.5" cy="18.5" r="2.5" opacity="0.85" />
    </svg>
  );
}

// Optical wordmark: light + bold split inspired by the existing
// "Bellhopt" pill. Renders the brand as a single typographic unit so the
// pill no longer prints a literal placeholder string.
function WordmarkSvg({ className = 'h-3.5', title = 'Bellhopt' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 132 24"
      className={className}
      role="img"
      aria-label={title}
      fill="currentColor"
    >
      <title>{title}</title>
      <text
        x="0"
        y="18"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
        fontSize="18"
        fontWeight="700"
        letterSpacing="-0.4"
      >
        Bellhopt
      </text>
    </svg>
  );
}

/**
 * BellhoptLogo
 *
 * @param {'mark'|'wordmark'} variant - Visual variant.
 * @param {'sm'|'md'|'lg'} size - For the `mark` variant only. Controls
 *   the gradient tile size. Defaults to `md` (36×36).
 * @param {boolean} withChrome - When true (default), wraps the mark in
 *   the existing premium gradient tile / pill. When false, returns the
 *   bare SVG so callers that already have their own tile can reuse the
 *   mark without double-wrapping.
 * @param {string} className - Forwarded to the outer wrapper.
 */
export default function BellhoptLogo({
  variant = 'mark',
  size = 'md',
  withChrome = true,
  className = '',
  title = 'Bellhopt',
}) {
  if (variant === 'wordmark') {
    if (!withChrome) {
      return <WordmarkSvg className={className || 'h-3.5'} title={title} />;
    }
    return (
      <span
        className={`h-9 rounded-full px-3 inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 text-white shadow-sm border border-white/20 ${className}`}
      >
        <WordmarkSvg className="h-3.5" title={title} />
        <span className="w-2 h-2 rounded-full bg-white/80" aria-hidden="true" />
      </span>
    );
  }

  const sizeClasses = {
    sm: { box: 'w-7 h-7 rounded-lg', icon: 'h-4 w-4' },
    md: { box: 'w-9 h-9 rounded-xl', icon: 'h-5 w-5' },
    lg: { box: 'w-11 h-11 rounded-xl', icon: 'h-6 w-6' },
  }[size] || { box: 'w-9 h-9 rounded-xl', icon: 'h-5 w-5' };

  if (!withChrome) {
    return <MarkSvg className={className || sizeClasses.icon} title={title} />;
  }

  return (
    <span
      className={`inline-flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/20 ${sizeClasses.box} ${className}`}
      aria-hidden={title ? undefined : true}
    >
      <MarkSvg className={sizeClasses.icon} title={title} />
    </span>
  );
}
